/**
 * Axios Configuration
 * Configure axios instance with base URL, interceptors, and default headers
 */

import axios from 'axios';

// Create axios instance with default config
// Default to production backend - no localhost fallback
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://a2amycodeai-server.appsokay.net/api/v1',
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('accessToken');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add request timestamp for debugging
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      config.metadata = { startTime: new Date() };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Log request duration in debug mode
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true' && response.config.metadata) {
      const duration = new Date() - response.config.metadata.startTime;
      console.log(`[API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
    }
    
    // Backend returns: { status: 'success', data: {...}, message: '...' }
    // Return the data object directly (which contains status, data, message)
    if (response.data && typeof response.data === 'object') {
      return response.data;
    }
    
    // Fallback: return as-is if not an object
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Clear tokens and redirect to login
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/auth/refresh-token`,
          { refreshToken }
        );

        const { accessToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden - Show error message
    if (error.response?.status === 403) {
      console.error('[API] Access Forbidden:', error.response.data?.message || 'You do not have permission to access this resource');
    }

    // Handle 429 Rate Limit - Show retry message with Retry-After
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || '60';
      console.warn(`[API] Rate limit exceeded. Please retry after ${retryAfter} seconds`);
      error.retryAfter = retryAfter;
    }

    // Handle 500 Server Error - Show generic error message
    if (error.response?.status >= 500) {
      console.error('[API] Server Error:', error.response.data?.message || 'An unexpected server error occurred');
    }

    // Handle network errors (no response from server)
    if (!error.response) {
      console.error('[API] Network Error:', error.message);
      // Return a user-friendly error message
      const networkError = new Error('Network Error: Unable to connect to the server. Please check your internet connection and try again.');
      networkError.isNetworkError = true;
      return Promise.reject(networkError);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

